// PDF export of a patient's authorized medication catalog.
// Standalone (mirrors the institutional style of lib/pdf.js).

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = {
  navy: [30, 58, 138],
  blue: [29, 78, 216],
  light: [239, 246, 255],
  ink: [15, 23, 42],
  muted: [100, 116, 139],
  white: [255, 255, 255],
};

let _logoCache = null;
async function loadLogo() {
  if (_logoCache !== null) return _logoCache || null;
  try {
    const res = await fetch("/assets/logo.svg", { cache: "force-cache" });
    if (!res.ok) throw new Error("logo fetch failed");
    const svgText = await res.text();
    const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = 1024;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });
    _logoCache = dataUrl;
    return _logoCache;
  } catch {
    _logoCache = false;
    return null;
  }
}

const IND_LABEL = { rx: "Indicacion medica (Rx)", prn: "Razon necesaria (PRN)" };

export async function exportMedCatalogPDF(patientName, meds) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const logo = await loadLogo();

  // Header band
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, w, 30, "F");
  if (logo) {
    doc.setFillColor(...BRAND.white);
    doc.circle(21, 15, 11, "F");
    doc.addImage(logo, "PNG", 11, 5, 20, 20, undefined, "FAST");
  }
  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("Ciudad Enfermeria", 38, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Catalogo de medicamentos autorizados", 38, 20);

  let y = 40;
  doc.setTextColor(...BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(`Paciente: ${patientName || "-"}`, 12, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(
    `Generado: ${new Date().toLocaleString("es-MX")}  ·  ${meds.length} medicamento(s) autorizado(s)`,
    12,
    y
  );
  y += 6;

  autoTable(doc, {
    startY: y,
    head: [["#", "Medicamento", "Dosis", "Via", "Indicacion", "Horarios", "Interv. min", "Notas / criterio de uso"]],
    body: meds.map((m, i) => [
      i + 1,
      m.name || "-",
      m.default_dose || "-",
      m.default_via || "-",
      IND_LABEL[m.default_indication] || m.default_indication || "-",
      (m.schedule_times || []).join(", ") || "-",
      m.min_interval_hours ? `${m.min_interval_hours} h` : "-",
      m.notes || "",
    ]),
    theme: "grid",
    headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 8 },
    bodyStyles: { fontSize: 8.5, textColor: BRAND.ink, valign: "top" },
    alternateRowStyles: { fillColor: BRAND.light },
    columnStyles: {
      0: { cellWidth: 8, halign: "center", fontStyle: "bold" },
      1: { cellWidth: 38, fontStyle: "bold" },
      2: { cellWidth: 22 },
      3: { cellWidth: 24 },
      4: { cellWidth: 26 },
      5: { cellWidth: 24 },
      6: { cellWidth: 16, halign: "center" },
      7: { cellWidth: "auto" },
    },
    margin: { left: 12, right: 12 },
  });

  const endY = doc.lastAutoTable.finalY + 8;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(
    "Solo el personal de enfermeria puede administrar medicamentos de esta lista, segun indicacion medica. Documento generado por la app Programa de Cuidados.",
    12,
    Math.min(endY, doc.internal.pageSize.getHeight() - 10),
    { maxWidth: w - 24 }
  );

  const now = new Date();
  const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const safe = (patientName || "paciente").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
  const fname = `medicamentos_${safe}_${stamp}.pdf`;
  doc.save(fname);
  return fname;
}
